import React from "react";

// Découpe un titre en mettant `highlight` en <strong>. Si highlight n'est pas trouvé,
// on met en gras la première moitié logique (avant la première préposition).
// Le but : garder la signature simple (title + highlight) et toujours produire
// un rendu "mot-clé en gras" cohérent avec la maquette.
export function SectionTitle({ title, highlight, subtitle, className = "" }) {
    if (!title) return null;

    let before = "", bold = "", after = "";
    if (highlight && title.includes(highlight)) {
        const idx = title.indexOf(highlight);
        before = title.slice(0, idx);
        bold = highlight;
        after = title.slice(idx + highlight.length);
    } else {
        const parts = title.split(" ");
        const half = Math.ceil(parts.length / 2);
        bold = parts.slice(0, half).join(" ");
        after = " " + parts.slice(half).join(" ");
    }

    return (
        <header className={`tr-ui-head ${className}`}>
            <h2 className="tr-ui-title">
                {before}
                <strong>{bold}</strong>
                {after}
            </h2>
            {subtitle && <p className="tr-ui-sub">{subtitle}</p>}
        </header>
    );
}

// Styles partagés injectés une seule fois par les composants qui les utilisent.
export const sharedStyles = `
.tr-ui-section {
  padding: clamp(32px, 6vw, 72px) clamp(16px, 4vw, 32px);
  max-width: 1200px;
  margin: 0 auto;
}
.tr-ui-head {
  text-align: center;
  margin-bottom: clamp(20px, 3vw, 32px);
}
.tr-ui-title {
  margin: 0;
  font-size: clamp(22px, 3.2vw, 34px);
  font-weight: 400;
  color: #1a1a2e;
  letter-spacing: -.01em;
  line-height: 1.15;
}
.tr-ui-title strong {
  font-weight: 800;
}
.tr-ui-sub {
  margin: 8px 0 0;
  font-size: 15px;
  color: #6b7280;
}
.tr-ui-seemore {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: clamp(20px, 3vw, 32px);
  font-size: 15px;
  color: #1a1a2e;
  text-decoration: none;
}
.tr-ui-seemore__arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px; height: 28px;
  border-radius: 50%;
  background: var(--primary, #1b579e);
  color: white;
  transition: transform .15s ease;
}
.tr-ui-seemore:hover .tr-ui-seemore__arrow {
  transform: translateX(3px);
}
[dir="rtl"] .tr-ui-seemore:hover .tr-ui-seemore__arrow {
  transform: translateX(-3px);
}
`;

// Hash stable d'une string → permet un tirage random déterministe basé sur l'org.
export function stableHash(str) {
    let h = 0;
    for (let i = 0; i < str.length; i++) {
        h = ((h << 5) - h) + str.charCodeAt(i);
        h |= 0;
    }
    return Math.abs(h);
}

// Résolution d'URL d'image : absolue ou relative à ASSET_URL.
export function resolveImage(img, ASSET_URL) {
    if (!img) return null;
    if (/^https?:\/\//.test(img)) return img;
    return `${ASSET_URL}${img}`;
}

// Styles communs aux grilles de cards sombres (Kosher, Hotels).
export const placeCardStyles = `
.tr-pgrid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 320px));
  gap: clamp(14px, 2vw, 24px);
  justify-content: center;
}
.tr-pgrid--1 { grid-template-columns: minmax(240px, 320px); }
.tr-pgrid--2 { grid-template-columns: repeat(2, minmax(0, 320px)); }
.tr-pgrid--3 { grid-template-columns: repeat(3, minmax(0, 320px)); }
.tr-pgrid--4 { grid-template-columns: repeat(4, minmax(0, 300px)); }
@media (max-width: 900px) {
  .tr-pgrid--3, .tr-pgrid--4 { grid-template-columns: repeat(2, minmax(0, 320px)); }
}
@media (max-width: 560px) {
  .tr-pgrid--2, .tr-pgrid--3, .tr-pgrid--4 { grid-template-columns: minmax(0, 320px); }
}

.tr-pcard {
  position: relative;
  aspect-ratio: 3/4;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0,0,0,.08), 0 1px 4px rgba(0,0,0,.04);
  transition: transform .25s ease, box-shadow .25s ease;
  background: #1a1a2e;
  text-decoration: none;
  color: inherit;
  display: block;
}
.tr-pcard:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 32px rgba(0,0,0,.16), 0 2px 6px rgba(0,0,0,.06);
}
.tr-pcard__img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform .4s ease;
}
.tr-pcard:hover .tr-pcard__img { transform: scale(1.06); }
.tr-pcard__img-placeholder {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  color: #9ca3af;
  background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
}
.tr-pcard__wash {
  position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(9,18,26,0) 30%, rgba(9,18,26,.55) 60%, rgba(9,18,26,.92) 100%);
}
.tr-pcard__body {
  position: absolute; inset: auto 0 0 0;
  padding: 16px 18px 18px;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.tr-pcard__badges { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
.tr-pcard__badge {
  display: inline-block;
  font-size: 11px; font-weight: 700;
  padding: 4px 10px;
  border-radius: 6px;
  letter-spacing: .02em;
  background: var(--primary, #1b579e);
  color: white;
}
/* Couleurs alignées sur la convention culinaire :
   - Viande = rouge (couleur du sang / chair crue)
   - Lacté  = bleu (couleur lait / produits laitiers)
   - Parvé  = noir (neutre, ni viande ni lacté — code "sans" classique)
   - Poisson = cyan (référence aquatique) */
.tr-pcard__badge--meat  { background: #dc2626; color: white; }
.tr-pcard__badge--dairy { background: #3b82f6; color: white; }
.tr-pcard__badge--parve { background: #111827; color: white; border: 1px solid rgba(255,255,255,.3); }
.tr-pcard__badge--fish  { background: #06b6d4; color: white; }
.tr-pcard__name {
  font-size: 17px;
  font-weight: 800;
  line-height: 1.25;
  color: white;
}
.tr-pcard__meta {
  display: flex; align-items: flex-start; gap: 6px;
  font-size: 12px;
  color: rgba(255,255,255,.88);
  line-height: 1.35;
  padding-top: 8px;
  border-top: 1px solid rgba(255,255,255,.15);
}
.tr-pcard__meta-icon { flex-shrink: 0; margin-top: 1px; }

.tr-pcard--inactive { opacity: .85; }
.tr-pcard__inactive-note {
  margin-top: 4px;
  background: rgba(255,255,255,.14);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255,255,255,.92);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
`;
